import { getStoreContext } from "../../../constants";
import { ProductObj, StepConditionType, StepsType, SubStepType } from "../../Types";

export const checkProductLimit = (
    conditions: {
        basis: "items_count" | "bundle_total";
        type: "max_products" | "exact_products" | "min_products";
        limit: number;
    }[],
    itemsCount: number,
    totalPrice: number,
): boolean => {
    if (!conditions?.length) return true; // no condition → always valid

    return conditions.every((condition) => {
        if (condition.basis === "bundle_total") {
            const convertedLimit = convertValueToLocal(condition.limit);
            switch (condition.type) {
                case "min_products":
                    return totalPrice >= convertedLimit;
                case "max_products":
                    return totalPrice <= convertedLimit;
                case "exact_products":
                    return totalPrice == convertedLimit;
                default:
                    return true;
            }
        } else {
            switch (condition.type) {
                case "min_products":
                    return itemsCount >= condition.limit;
                case "max_products":
                    return itemsCount <= condition.limit;
                case "exact_products":
                    return itemsCount == condition.limit;
                default:
                    return true;
            }
        }
    });
};

export const convertValueToLocal = (val: number, roundDecimal: boolean = false): number => {
    const converted = val * (window?.kefi_bundles?.localization?.rate || 1);
    return roundDecimal ? +Math.round(converted * 100) / 100 : converted;
};

export const roundN = (n: number | string, decimal: number = 2) =>
    Math.round(Number(n) * Math.pow(10, decimal)) / Math.pow(10, decimal);

export function getCurrentPageSelectCount(
    selectedProducts: ProductObj[] | undefined,
    activeStep: number,
    activeSubStep: number,
): number {
    if (!selectedProducts?.length) return 0;

    return selectedProducts.reduce((count, product) => {
        const pPage = (product as any).page;
        const pSubStep = (product as any).subStep;
        if (+pPage === activeStep && (activeSubStep === -1 || +pSubStep === activeSubStep) && !product?.isAddon) {
            if (product?.exclude_from_step_conditions) {
                return count;
            }
            return count + (product.selectedVariants?.length || 0);
        }
        return count;
    }, 0);
}

export function getCurrentPageSelectTotal(
    selectedProducts: ProductObj[] | undefined,
    activeStep: number,
    activeSubStep: number,
): number {
    if (!selectedProducts?.length) return 0;

    const total = selectedProducts.reduce((count, product) => {
        const pPage = (product as any).page;
        const pSubStep = (product as any).subStep;
        if (+pPage === activeStep && (activeSubStep === -1 || +pSubStep === activeSubStep)) {
            if (product?.exclude_from_step_conditions) {
                return count;
            }
            const variantTotal =
                product.selectedVariants?.reduce((sum, variant) => {
                    return sum + (+variant?.price || 0) * (variant.quantity || 1);
                }, 0) || 0;

            return count + variantTotal;
        }
        return count;
    }, 0);
    return convertValueToLocal(total);
}

export const shouldDisableCta = (
    conditions: {
        basis: "items_count" | "bundle_total";
        type: "max_products" | "exact_products" | "min_products";
        limit: number;
    }[],
    itemsCount: number,
    totalPrice: number,
): boolean => {
    const filterConditions = conditions?.filter((c) => c.type != "min_products");
    if (!filterConditions?.length) return false; // no conditions → CTA stays enabled

    return filterConditions.every((condition) => {
        if (condition.basis === "bundle_total") {
            switch (condition.type) {
                case "max_products":
                    return totalPrice >= condition.limit;
                case "exact_products":
                    return totalPrice == condition.limit;
                default:
                    return false;
            }
        } else {
            switch (condition.type) {
                case "max_products":
                    return itemsCount >= condition.limit;
                case "exact_products":
                    return itemsCount == condition.limit;
                default:
                    return false;
            }
        }
    });
};

export const getValidErrorMsg = (
    step: StepsType | SubStepType | any,
    condition: StepConditionType | any,
    itemsCount: number,
    totalPrice: number,
    currency: any,
    currencyToSymbols: any,
) => {
    let errMsg: string = "";

    const basis = condition.basis || "items_count";
    const productCount = `${condition.limit}`;
    const stepConditionType = condition.type;
    const stepLimit = condition.limit;
    const convertedStepLimit = convertValueToLocal(stepLimit, true);
    const formattedPrice = currencyToSymbols[currency] + convertedStepLimit;
    if (basis == "bundle_total") {
        if (stepConditionType == "min_products" && totalPrice < convertedStepLimit) {
            errMsg = condition?.msg
                ?.replace("{{amount}}", `${formattedPrice}`)
                ?.replace("{amount}", `${formattedPrice}`)
                ?.replace("{substep_label}", `${step.step_label}`)
                ?.replace("{category_label}", `${step.step_label}`)
                ?.replace("{step_label}", `${step.step_label}`);
        } else if (stepConditionType == "max_products" && totalPrice > stepLimit) {
            errMsg = condition?.msg
                ?.replace("{{amount}}", `${formattedPrice}`)
                ?.replace("{amount}", `${formattedPrice}`)
                ?.replace("{substep_label}", `${step.step_label}`)
                ?.replace("{category_label}", `${step.step_label}`)
                ?.replace("{step_label}", `${step.step_label}`);
        } else if (stepConditionType == "exact_products" && totalPrice != stepLimit) {
            errMsg = condition?.msg
                ?.replace("{{amount}}", `${formattedPrice}`)
                ?.replace("{amount}", `${formattedPrice}`)
                ?.replace("{substep_label}", `${step.step_label}`)
                ?.replace("{category_label}", `${step.step_label}`)
                ?.replace("{step_label}", `${step.step_label}`);
        } else {
            errMsg = ``;
        }
    } else {
        if (stepConditionType == "min_products" && itemsCount < stepLimit) {
            errMsg = condition?.msg
                ?.replace("{{productCount}}", `${productCount}`)
                ?.replace("{productCount}", `${productCount}`)
                ?.replace("{substep_label}", `${step.step_label}`)
                ?.replace("{category_label}", `${step.step_label}`)
                ?.replace("{step_label}", `${step.step_label}`);
        } else if (stepConditionType == "max_products" && itemsCount > stepLimit) {
            errMsg = condition?.msg
                ?.replace("{{productCount}}", `${productCount}`)
                ?.replace("{productCount}", `${productCount}`)
                ?.replace("{substep_label}", `${step.step_label}`)
                ?.replace("{category_label}", `${step.step_label}`)
                ?.replace("{step_label}", `${step.step_label}`);
        } else if (stepConditionType == "exact_products" && itemsCount != stepLimit) {
            errMsg = condition?.msg
                ?.replace("{{productCount}}", `${productCount}`)
                ?.replace("{productCount}", `${productCount}`)
                ?.replace("{substep_label}", `${step.step_label}`)
                ?.replace("{category_label}", `${step.step_label}`)
                ?.replace("{step_label}", `${step.step_label}`);
        } else {
            errMsg = "";
        }
    }

    return errMsg;
};

export const renderErrorsOnButton = (msgArray: string[], asDivs: boolean = false): any => {
    if (!msgArray?.length) return asDivs ? [] : "";
    const formattedLines = msgArray?.length > 1 ? msgArray.map((str, idx) => `${idx + 1}. ${str}`) : [msgArray[0]];
    if (asDivs) {
        return formattedLines;
    } else {
        return formattedLines.join("\n");
    }
};

//for product card size width
export const getBBCardWidth = (size: "small" | "medium" | "large", screen: "mobile" | "desktop") => {
    const sizeMap = {
        mobile: {
            small: "140px",
            medium: "160px",
            large: "175px",
        },
        desktop: {
            small: "190px",
            medium: "210px",
            large: "230px",
        },
    };

    return sizeMap[screen][size];
};

//for product wrapper gap
export const getGapForSize = (size: "small" | "medium" | "large", isMobile: boolean) => {
    if (isMobile) {
        switch (size) {
            case "large":
                return "10px";
            case "medium":
                return "15px";
            case "small":
                return "16px";
            default:
                return "15px";
        }
    } else {
        switch (size) {
            case "large":
                return "30px";
            case "medium":
                return "20px";
            case "small":
                return "16px";
            default:
                return "20px";
        }
    }
};

// this is being used in BB for conditional_products
export const applyConditionsBB = (
    productConditions: {
        included_products: ProductObj[];
        hidden_products: ProductObj[];
        hidden_steps?: string[];
        action_type?: "products" | "steps";
    }[],
    selectedProducts: ProductObj[],
    listProducts: ProductObj[],
) => {
    let updatedList = [...listProducts];

    productConditions.forEach((condition) => {
        const includedVariantIds =
            condition.included_products.flatMap((p) => {
                return p.variants?.flatMap((v) => v.id);
            }) || [];

        const hiddenVariantIds =
            condition.hidden_products.flatMap((p) => {
                return p.variants?.flatMap((v) => v.id);
            }) || [];

        const selectedVariantIds =
            selectedProducts?.flatMap((p) => {
                return p?.selectedVariants?.flatMap((v) => v.id);
            }) || [];

        const isProductAction = !condition.action_type || condition.action_type === "products";

        if (isProductAction && selectedVariantIds?.some((id) => includedVariantIds.includes(id))) {
            updatedList = updatedList.map((product) => {
                const updatedVariants = product.variants.filter((v) => !hiddenVariantIds.includes(v.id));
                return { ...product, variants: updatedVariants };
            });

            updatedList = updatedList.filter((product) => product.variants.length > 0);
        }
    });

    return updatedList;
};

export const getHiddenStepsBB = (
    productConditions: {
        included_products: ProductObj[];
        hidden_steps?: string[];
        hidden_substeps?: string[];
        action_type?: "products" | "steps";
    }[],
    selectedProducts: ProductObj[],
) => {
    const hiddenSteps = new Set<string>();
    const hiddenSubSteps = new Set<string>();

    productConditions?.forEach((condition) => {
        if (condition.action_type === "steps") {
            const includedVariantIds =
                condition.included_products.flatMap((p) => {
                    return p.variants?.flatMap((v) => v.id);
                }) || [];

            const selectedVariantIds =
                selectedProducts?.flatMap((p) => {
                    return p?.selectedVariants?.flatMap((v) => v.id);
                }) || [];

            if (selectedVariantIds?.some((id) => includedVariantIds.includes(id))) {
                condition.hidden_steps?.forEach((stepLabel) => hiddenSteps.add(stepLabel));
                condition.hidden_substeps?.forEach((subStepKey) => hiddenSubSteps.add(subStepKey));
            }
        }
    });

    return { hiddenSteps: Array.from(hiddenSteps), hiddenSubSteps: Array.from(hiddenSubSteps) };
};

// this is being used in BB for conditional_products
export const removeConditionalProductIfAny = (
    productConditions: {
        included_products: ProductObj[];
        hidden_products: ProductObj[];
        hidden_steps?: string[];
        action_type?: "products" | "steps";
    }[],
    selectedProducts: ProductObj[],
    productToAdd: ProductObj,
) => {
    let updatedList = [...selectedProducts];

    productConditions.forEach((condition) => {
        const includedVariantIds =
            condition.included_products.flatMap((p) => {
                return p.variants?.flatMap((v) => v.id);
            }) || [];

        const hiddenVariantIds =
            condition.hidden_products.flatMap((p) => {
                return p.variants?.flatMap((v) => v.id);
            }) || [];

        const selectedVariantIds =
            selectedProducts?.flatMap((p) => {
                return p?.selectedVariants?.flatMap((v) => v.id);
            }) || [];
        selectedVariantIds.push(productToAdd.selectedVariants[0].id);

        const isProductAction = !condition.action_type || condition.action_type === "products";

        if (
            isProductAction &&
            selectedVariantIds?.some((id) => includedVariantIds.includes(id)) &&
            selectedVariantIds?.some((id) => hiddenVariantIds.includes(id))
        ) {
            updatedList = updatedList.map((product) => {
                const filteredSelectedVariants =
                    product.selectedVariants?.filter((variant) => !hiddenVariantIds.includes(variant.id)) || [];

                return {
                    ...product,
                    selectedVariants: filteredSelectedVariants,
                };
            });

            updatedList = updatedList.filter((product) => product.selectedVariants.length > 0);
        }
    });

    return updatedList;
};

// this is being used in pre-selected-products BB
export const getUpdatedVariants = async (
    productList: any[],
    storefrontAccessToken: string,
    steps: StepsType[],
    standalone: boolean,
) => {
    const productIds = productList.reduce((acc: string[], p: any) => {
        acc.push(p.id);
        return acc;
    }, []);

    if (!productIds.length) return [];

    const context = getStoreContext(standalone);

    const query = `
        query getProductsWithVariants($ids: [ID!]!) ${context} {
            nodes(ids: $ids) {
                ... on Product {
                    id
                    title
                    handle
                    description
                    descriptionHtml
                    options {
                        id
                        name
                        values
                    }
                    variants(first: 100) {
                        nodes {
                            id
                            title
                            price {
                                amount
                            }
                            compareAtPrice {
                                amount
                            }
                            quantityAvailable
                            availableForSale
                            image {
                                originalSrc
                            }
                            selectedOptions {
                                name
                                value
                            }
                        }
                    }
                    images(first: 5) {
                        nodes {
                            id
                            originalSrc
                            altText
                        }
                    }
                    metafield(namespace: "custom", key: "swatches") {
                        value
                    }
                    collections(first: 50){
                        nodes {
                            id
                        }
                    }
                }
            }
        }
    `;

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("X-Shopify-Storefront-Access-Token", storefrontAccessToken);

    const body = JSON.stringify({ query, variables: { ids: productIds } });

    try {
        const shop = !standalone ? shopify.config.shop : window.kefi_bundles.shop;
        const res = await fetch(`https://${shop}/api/2025-07/graphql.json`, {
            method: "POST",
            headers,
            body,
        });

        const json = await res.json();
        const productNodes = json?.data?.nodes?.filter(Boolean);

        if (!productNodes?.length) return [];

        const productMap: Record<string, any> = {};

        for (const product of productNodes) {
            const productId = product.id;
            const variants = product.variants.nodes;
            const collectionIds = product.collections.nodes.map((c: any) => c.id);

            let stepIndex = -1;
            let subStepIndex = -1;
            let isFreeGiftStep = false;

            // finding which step the product belongs to
            for (let i = 0; i < steps.length; i++) {
                const step = steps[i];

                if (step.enable_subSteps && Array.isArray(step.subSteps)) {
                    const matchedSubStepIndex = step.subSteps.findIndex((subStep) => {
                        const productMatch = subStep.products?.some((p: any) => p.id === productId);
                        const collectionMatch = subStep.collections?.some((c: any) => collectionIds.includes(c.id));
                        return productMatch || collectionMatch;
                    });

                    if (matchedSubStepIndex !== -1) {
                        stepIndex = i;
                        subStepIndex = matchedSubStepIndex;
                        isFreeGiftStep = step.step_content_type === "free_gift";
                        break;
                    }
                } else {
                    const productMatch = step.products?.some((p: any) => p.id === productId);
                    const collectionMatch = step.collections?.some((c: any) => collectionIds.includes(c.id));

                    if (productMatch || collectionMatch) {
                        stepIndex = i;
                        isFreeGiftStep = step.step_content_type === "free_gift";
                        break;
                    }
                }
            }

            const firstAvailable = variants.find((v: any) => v.availableForSale);
            if (!firstAvailable) continue;

            const quantityAvailable = firstAvailable.quantityAvailable <= 0 ? 1000 : firstAvailable.quantityAvailable;
            const desiredQty = Math.min(
                Number(productList.find((i) => i.id === productId)?.quantity || 1),
                quantityAvailable,
            );

            if (desiredQty <= 0) continue;

            const selectedVariants = [];

            for (let i = 0; i < desiredQty; i++) {
                selectedVariants.push({
                    id: firstAvailable.id,
                    title:
                        firstAvailable?.title?.toLowerCase() === "default title"
                            ? product?.title
                            : firstAvailable?.title,
                    get displayName() {
                        return this.title;
                    },
                    price: Number(firstAvailable.price?.amount ?? 0),
                    compareAtPrice: firstAvailable.compareAtPrice?.amount ?? firstAvailable.price?.amount ?? null,
                    quantityAvailable: firstAvailable.quantityAvailable,
                    availableForSale: firstAvailable.availableForSale,
                    image: firstAvailable.image ? { originalSrc: firstAvailable.image?.originalSrc } : null,
                    selectedOptions: firstAvailable.selectedOptions,
                    quantity: 1,
                    page: String(stepIndex),
                    subStep: subStepIndex !== -1 ? String(subStepIndex) : "0",
                    isPreselected: true,
                    freeGift: isFreeGiftStep,
                    collectionIds: product?.collections.nodes.map((c: any) => c.id),
                });
            }

            productMap[productId] = {
                id: productId,
                title: product.title,
                productTitle: "Mana",
                handle: product.handle,
                description: product.description,
                descriptionHtml: product.descriptionHtml,
                options: product.options,
                variants: variants.map((pv: any) => ({
                    ...pv,
                    price: pv.price?.amount ?? "0",
                    compareAtPrice: pv.compareAtPrice?.amount ?? pv.price?.amount ?? null,
                })),
                images: product.images.nodes,
                selectedVariants,
                subscriptions: [],
                oneTimePurchase: true,
                isSubscribed: false,
                page: String(stepIndex),
                subStep: subStepIndex !== -1 ? String(subStepIndex) : "0",
                quantity: 1,
                isPreselected: true,
                freeGift: isFreeGiftStep,
            };
        }
        return Object.values(productMap).filter((product: any) => product?.selectedVariants?.length > 0);
    } catch (err) {
        console.error("Shopify fetch error:", err);
        return [];
    }
};

export const shouldGoToNext = (
    conditions: {
        basis: "items_count" | "bundle_total";
        type: "max_products" | "exact_products" | "min_products";
        limit: number;
    }[],
    itemsCount: number,
    totalPrice: number,
) => {
    if (!conditions?.length) return false;

    return conditions.every((condition) => {
        const value = condition.basis === "bundle_total" ? convertValueToLocal(totalPrice) : itemsCount;

        switch (condition.type) {
            case "min_products":
                return false;

            case "max_products":
                return value >= +condition.limit;

            case "exact_products":
                return value === +condition.limit;

            default:
                return false;
        }
    });
};
